import { useLingui } from "@lingui/react/macro";
import { Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { LabelFieldDefinition, LabelFieldKey } from "@/types/label-template";
import { useLabelFieldNames } from "./useLabelFieldNames";

interface LabelFieldPickerProps {
  available: LabelFieldDefinition[];
  selected: LabelFieldKey[];
  onChange: (fields: LabelFieldKey[]) => void;
  disabled?: boolean;
}

/**
 * Ticks the fields a label prints.
 *
 * Required fields render locked rather than hidden: a cook needs to see that the
 * name and the allergens are on the label, and hiding them would read as an
 * oversight rather than as a rule.
 */
export function LabelFieldPicker({
  available,
  selected,
  onChange,
  disabled = false,
}: LabelFieldPickerProps) {
  const { t } = useLingui();
  const names = useLabelFieldNames();
  const chosen = new Set(selected);

  const toggle = (key: LabelFieldKey) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Order comes from the catalog, which is the order the label prints in.
    onChange(available.filter((f) => next.has(f.key) || f.required).map((f) => f.key));
  };

  return (
    <div className="flex flex-col gap-2">
      {available.map((field) => {
        const id = `label-field-${field.key}`;
        const isOn = field.required || chosen.has(field.key);
        return (
          <div
            key={field.key}
            className="flex items-center gap-3 rounded-lg border border-(--color-border) px-3 py-2.5"
          >
            <Checkbox
              id={id}
              checked={isOn}
              disabled={disabled || field.required}
              onCheckedChange={() => toggle(field.key)}
            />
            <Label htmlFor={id} className="flex-1 cursor-pointer text-sm font-normal">
              {names[field.key]}
            </Label>
            {field.required && (
              <span className="flex items-center gap-1 text-xs text-(--color-text-secondary)">
                <Lock className="size-3" />
                {t`Always printed`}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
